/**
 * MailboxSnoozedDialog — lists currently-snoozed messages for the account and
 * lets the user resurface one early.
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useSnoozedMessages, useUnsnoozeMessage } from '../hooks/useMailbox';

interface MailboxSnoozedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId?: string;
}

export function MailboxSnoozedDialog({ open, onOpenChange, accountId }: MailboxSnoozedDialogProps) {
  const { data: messages, isLoading } = useSnoozedMessages(accountId, open);
  const unsnooze = useUnsnoozeMessage();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Snoozed</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : !messages || messages.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No snoozed messages.</p>
        ) : (
          <ul className="space-y-2">
            {messages.map((m) => (
              <li
                key={m.messageId}
                className="flex items-center justify-between gap-3 rounded border px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{m.subject || '(no subject)'}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {m.fromName || m.fromAddress}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={unsnooze.isPending}
                  onClick={() => unsnooze.mutate(m.messageId)}
                >
                  Unsnooze
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
